import { useCallback, useRef, useState } from 'react';
import {
  DEFAULT_SCRATCH_BLOCK_TYPE,
  SCRATCH_MAX_BLOCKS,
  SCRATCH_BLOCK_TYPES,
  SCRATCH_TYPE_LABEL,
  type ScratchBlock,
  type ScratchBlockType,
  type ScratchDoc,
} from '../../../shared/scratch';
import { ulid } from '../../../shared/ulid';
import { reorder } from '../../lib/reorder';
import { showToast } from '../Toaster';
import ScratchBlockCard from './ScratchBlockCard';
import type { PromoteAction } from './PromoteMenu';
import { scratchBodyFromText } from './scratchText';
import type { ScratchSaveState } from './useScratchAutosave';

type Filter = ScratchBlockType | 'all';

interface Props {
  doc: ScratchDoc;
  onChange: (next: ScratchDoc) => void;
  onPromote: (block: ScratchBlock, action: PromoteAction) => void;
  onCollapse: () => void;
  saveState: ScratchSaveState;
  errorMsg: string | null;
  /** Hidden on mobile, where the Script / Scratch switch does the collapsing. */
  showCollapse?: boolean;
}

export default function ScratchpadPane({
  doc,
  onChange,
  onPromote,
  onCollapse,
  saveState,
  errorMsg,
  showCollapse = true,
}: Props) {
  const [filter, setFilter] = useState<Filter>('all');
  const [dragIndex, setDragIndex] = useState<number | null>(null);
  const [draft, setDraft] = useState('');
  const inputRef = useRef<HTMLInputElement>(null);
  const onChangeRef = useRef(onChange);
  const onPromoteRef = useRef(onPromote);
  onChangeRef.current = onChange;
  onPromoteRef.current = onPromote;

  // Undo-from-toast fires long after the click that scheduled it, so it has to
  // reinsert into whatever the pad looks like then, not the copy we closed over.
  const docRef = useRef(doc);
  docRef.current = doc;
  const dragIndexRef = useRef(dragIndex);
  dragIndexRef.current = dragIndex;

  const blocks = doc.blocks;
  const visible = filter === 'all' ? blocks : blocks.filter((b) => b.type === filter);
  const stale = saveState === 'stale';

  const setBlocks = useCallback((next: ScratchBlock[]) => {
    // Two block editors can report in the same React batch (both normalise on
    // mount). Advance the ref immediately so the second change builds on the
    // first instead of on the pre-batch doc and silently dropping it.
    const nextDoc = { ...docRef.current, blocks: next };
    docRef.current = nextDoc;
    onChangeRef.current(nextDoc);
  }, []);

  const patchBlock = useCallback((id: string, patch: Partial<ScratchBlock>) => {
    setBlocks(docRef.current.blocks.map((b) => (b.id === id ? { ...b, ...patch } : b)));
  }, [setBlocks]);

  const changeType = useCallback((id: string, type: ScratchBlockType) => {
    patchBlock(id, { type });
  }, [patchBlock]);

  const changeBody = useCallback((id: string, body: unknown) => {
    patchBlock(id, { body });
  }, [patchBlock]);

  const promoteBlock = useCallback((id: string, action: PromoteAction) => {
    const block = docRef.current.blocks.find((candidate) => candidate.id === id);
    if (block) onPromoteRef.current(block, action);
  }, []);

  function addBlock() {
    if (stale) return;
    const text = draft.trim();
    if (!text) return;
    if (docRef.current.blocks.length >= SCRATCH_MAX_BLOCKS) {
      showToast('error', `The scratchpad holds ${SCRATCH_MAX_BLOCKS} blocks at most. Delete some to add more.`);
      return;
    }
    const block: ScratchBlock = {
      id: ulid(),
      type: DEFAULT_SCRATCH_BLOCK_TYPE,
      body: scratchBodyFromText(text),
      used_at: null,
      created_at: Date.now(),
    };
    // New jots land on top: the thing you just thought of is the thing you are
    // still thinking about.
    setBlocks([block, ...docRef.current.blocks]);
    setDraft('');
    // A filter that hides the new block would make it look like nothing
    // happened, so adding always drops back to All.
    if (filter !== 'all' && filter !== block.type) setFilter('all');
    inputRef.current?.focus();
  }

  const duplicateBlock = useCallback((id: string) => {
    const block = docRef.current.blocks.find((candidate) => candidate.id === id);
    if (!block) return;
    if (docRef.current.blocks.length >= SCRATCH_MAX_BLOCKS) {
      showToast('error', `The scratchpad holds ${SCRATCH_MAX_BLOCKS} blocks at most. Delete some to add more.`);
      return;
    }
    const copy: ScratchBlock = {
      id: ulid(),
      type: block.type,
      body: block.body,
      used_at: null,
      created_at: Date.now(),
    };
    const next = docRef.current.blocks.slice();
    const at = next.findIndex((b) => b.id === block.id);
    next.splice(at < 0 ? next.length : at + 1, 0, copy);
    setBlocks(next);
  }, [setBlocks]);

  const deleteBlock = useCallback((id: string) => {
    const block = docRef.current.blocks.find((candidate) => candidate.id === id);
    if (!block) return;
    const index = docRef.current.blocks.findIndex((b) => b.id === id);
    if (index < 0) return;
    setBlocks(docRef.current.blocks.filter((b) => b.id !== block.id));
    showToast('success', 'Block deleted', {
      action: {
        label: 'Undo',
        onClick: () => {
          const next = docRef.current.blocks.slice();
          next.splice(Math.min(index, next.length), 0, block);
          setBlocks(next);
        },
      },
    });
  }, [setBlocks]);

  const moveBlock = useCallback((id: string, delta: number) => {
    const from = docRef.current.blocks.findIndex((b) => b.id === id);
    if (from < 0) return;
    setBlocks(reorder(docRef.current.blocks, from, from + delta));
  }, [setBlocks]);

  const restoreBlock = useCallback((id: string) => {
    patchBlock(id, { used_at: null });
  }, [patchBlock]);

  const dropOn = useCallback((targetIndex: number) => {
    const from = dragIndexRef.current;
    if (from === null || from === targetIndex) return;
    setBlocks(reorder(docRef.current.blocks, from, targetIndex));
    setDragIndex(null);
  }, [setBlocks]);

  const startDrag = useCallback((index: number) => setDragIndex(index), []);
  const endDrag = useCallback(() => setDragIndex(null), []);

  return (
    <section
      aria-label="Scratchpad"
      className="
        hsp-scratch-pane flex flex-col bg-card border border-line rounded-md overflow-hidden
        min-h-[420px] md:max-h-[calc(100vh-6rem)]
      "
    >
      <header className="shrink-0 px-3 pt-3 pb-2.5 bg-elevated border-b border-line">
        <div className="flex items-center justify-between gap-2">
          <h2 className="font-display text-[17px] font-semibold tracking-[-0.01em] text-fg">
            Scratchpad
          </h2>
          <div className="flex items-center gap-2">
            <SaveDot state={saveState} errorMsg={errorMsg} />
            {showCollapse && (
              <button
                type="button"
                onClick={onCollapse}
                title="Close the scratchpad (⌘⇧S)"
                className="
                  text-[12px] font-medium text-faint px-1.5 py-0.5 rounded-sm
                  hover:bg-line/50 hover:text-fg transition-colors duration-150
                "
              >
                Collapse ››
              </button>
            )}
          </div>
        </div>

        <p className="flex items-center gap-1.5 mt-1 font-mono text-[9px] uppercase tracking-[0.12em] text-gold-deep">
          <svg
            width="11"
            height="11"
            viewBox="0 0 24 24"
            fill="none"
            stroke="currentColor"
            strokeWidth="1.8"
            strokeLinecap="round"
            strokeLinejoin="round"
            aria-hidden
            className="shrink-0"
          >
            <rect x="4" y="10" width="16" height="11" rx="2" />
            <path d="M8 10V7a4 4 0 0 1 8 0v3" />
          </svg>
          Admin only · never sent to the client
        </p>

        {saveState === 'stale' && (
          <p className="mt-2 px-2 py-1.5 rounded-sm bg-error/10 border border-error/40 text-error text-[12px] leading-snug">
            Scratchpad changed in another tab. Changes here are no longer being saved.{' '}
            <button
              type="button"
              onClick={() => window.location.reload()}
              className="underline font-medium"
            >
              Reload
            </button>{' '}
            to continue.
          </p>
        )}

        <div className="flex flex-wrap gap-1 mt-2.5">
          <FilterChip label="All" on={filter === 'all'} onClick={() => setFilter('all')} />
          {SCRATCH_BLOCK_TYPES.map((type) => (
            <FilterChip
              key={type}
              label={SCRATCH_TYPE_LABEL[type]}
              on={filter === type}
              onClick={() => setFilter(type)}
            />
          ))}
        </div>
      </header>

      <div className="flex-1 min-h-0 overflow-y-auto p-2.5 flex flex-col gap-1.5">
        {blocks.length === 0 ? (
          <EmptyState />
        ) : visible.length === 0 ? (
          <p className="m-auto text-center text-faint text-[13px] leading-[1.55] max-w-[28ch]">
            Nothing of that type yet. Pick All to see the rest.
          </p>
        ) : (
          visible.map((block) => {
            const index = blocks.indexOf(block);
            return (
              <ScratchBlockCard
                key={block.id}
                block={block}
                index={index}
                total={blocks.length}
                dragging={dragIndex === index}
                editable={!stale}
                actionsDisabled={stale}
                onChangeType={changeType}
                onChangeBody={changeBody}
                onPromote={promoteBlock}
                onDuplicate={duplicateBlock}
                onDelete={deleteBlock}
                onRestore={restoreBlock}
                onMove={moveBlock}
                onDragStart={startDrag}
                onDrop={dropOn}
                onDragEnd={endDrag}
              />
            );
          })
        )}
      </div>

      <div className="shrink-0 flex items-center gap-2 px-2.5 py-2 bg-elevated border-t border-line">
        <input
          ref={inputRef}
          disabled={stale}
          value={draft}
          onChange={(e) => setDraft(e.target.value)}
          onKeyDown={(e) => {
            if (e.key !== 'Enter') return;
            e.preventDefault();
            addBlock();
          }}
          placeholder="Jot an idea…"
          aria-label="Jot an idea"
          className="
            flex-1 min-w-0 bg-card rounded-full border border-line
            px-3 py-1.5 text-[13px] text-fg placeholder:text-faint
            focus:outline-none focus:border-accent transition-colors duration-150
          "
        />
        <button
          type="button"
          onClick={addBlock}
          disabled={stale || !draft.trim()}
          aria-label="Add the block"
          title="Add the block"
          className="
            shrink-0 px-1.5 py-1 rounded-sm bg-card border border-line
            font-mono text-[10px] font-medium text-faint
          "
        >
          ⌘↵
        </button>
      </div>
    </section>
  );
}

function EmptyState() {
  return (
    <div className="m-auto flex flex-col items-center gap-2.5 text-center px-4 py-8">
      <span className="w-10 h-10 rounded-md bg-elevated text-faint flex items-center justify-center">
        <svg
          width="19"
          height="19"
          viewBox="0 0 24 24"
          fill="none"
          stroke="currentColor"
          strokeWidth="1.6"
          strokeLinecap="round"
          strokeLinejoin="round"
          aria-hidden
        >
          <rect x="4" y="4" width="16" height="17" rx="2" />
          <path d="M8 2v4M12 2v4M16 2v4" />
          <path d="M8 12h8M8 16h5" />
        </svg>
      </span>
      <p className="text-faint text-[13px] leading-[1.55] max-w-[30ch]">
        Nothing here yet. Jot hooks, angles, half-lines or pasted notes. Nothing in this pane
        reaches the client.
      </p>
    </div>
  );
}

function FilterChip({
  label,
  on,
  onClick,
}: {
  label: string;
  on: boolean;
  onClick: () => void;
}) {
  return (
    <button
      type="button"
      onClick={onClick}
      aria-pressed={on}
      className={`
        px-2 py-0.5 rounded-full border
        font-mono text-[9px] uppercase tracking-[0.12em] font-medium
        transition-colors duration-150
        ${
          on
            ? 'bg-accent border-accent text-accent-fg'
            : 'bg-card border-line text-faint hover:border-fg/30 hover:text-fg'
        }
      `}
    >
      {label}
    </button>
  );
}

function SaveDot({ state, errorMsg }: { state: ScratchSaveState; errorMsg: string | null }) {
  if (state === 'idle') return null;
  const label =
    state === 'saving'
      ? 'Saving…'
      : state === 'saved'
        ? 'Saved'
        : state === 'stale'
          ? 'Not saving'
          : 'Save failed';
  const danger = state === 'stale' || state === 'error';
  return (
    <span
      title={state === 'error' && errorMsg ? errorMsg : undefined}
      className={`inline-flex items-center gap-1.5 font-mono text-[9px] uppercase tracking-[0.12em] whitespace-nowrap ${
        danger ? 'text-error' : 'text-faint'
      }`}
    >
      <span
        aria-hidden
        className={`w-1.5 h-1.5 rounded-full ${danger ? 'bg-error' : 'bg-current'}`}
      />
      {label}
    </span>
  );
}
